import { MAX_SIDE_JUDGES } from '../../utils/tournament';
import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { assignSelfToBout, removeSelfFromBout, getTournamentReferees, assignRingReferee, assignBoutJury, removeBoutJury } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { useI18n } from '../../i18n';
import { inlineError } from '../../hooks/useApiCall';
import { useBusy } from '../../hooks/useBusy';
import { useLoad } from '../../hooks/useLoad';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { rejoinBoutOfficialsRoom } from '../../hooks/useSocket';
import { ClipboardIcon, XMarkIcon, UserPlusIcon } from '../shared/Icons';
import { PencilIcon, BellIcon } from '../shared/Icons';
import { personName } from '../../utils/personName';
import { boutSeatsOf } from '../../utils/boutSeats';
import type { Bout, BoutReferee, TournamentReferee } from '../../types';
import Notice from '../shared/Notice';
import PingModal from '../shared/PingModal';

interface BoutOfficialsProps {
  bout: Bout;
  /** #664 — the read that shows the new bout. It returns its promise (the page
   *  debounces the reload of the socket, which never rejects), so the lock of
   *  a seat action ends after the new state shows, and a failed read rejects. */
  refresh: () => Promise<unknown>;
}

export default function BoutOfficials({ bout, refresh }: BoutOfficialsProps) {
  const { hasRole, user } = useAuthStore();
  const { t } = useI18n();
  const afterWrite = useAfterWrite();
  // #664 (F40) — the official that the jury pings. PingModal holds the text and the lock.
  const [pingTarget, setPingTarget] = useState<{ id: string; first_name: string; defaultText: string } | null>(null);

  // assignment panel — #605, #664: one lock for each pair of seat actions, so a Claim and an Unclaim cannot overlap.
  const jurySeat = useBusy();
  const [juryError, setJuryError] = useState('');
  const sideSeat = useBusy();
  const [sideError, setSideError] = useState('');

  // ring referee editor
  const [editingRingRef, setEditingRingRef] = useState(false);
  const [selectedRingRefId, setSelectedRingRefId] = useState('');
  const ringRefSave = useBusy();
  const [ringRefError, setRingRefError] = useState('');

  const isJury    = hasRole('jury', 'admin');
  // #454 — the side-judge seat is the server's `isReferee`: referee or admin.
  // A jury-only user saw "Join side", and the server answered 403.
  const canJudge  = hasRole('referee', 'admin');

  const hasVerdict        = !!bout.verdict;
  const seats             = boutSeatsOf(bout, user?.id);
  const juryAssigned      = !!bout.jury_id;
  const sideJudges: BoutReferee[] = bout.referees ?? [];
  const sideJudgeFull     = sideJudges.length >= MAX_SIDE_JUDGES;
  const boutActive        = bout.status !== 'scored' && !hasVerdict;

  // #664 — the ping target goes when the bout leaves 'current' or the official leaves the seat.
  // The bell shows in those two cases only, and PingModal would send a ping for an old seat.
  const sideJudgeKey = sideJudges.map(r => r.id).join(',');
  useEffect(() => {
    if (!pingTarget) return;
    const seated = pingTarget.id === bout.ring_referee_id || sideJudgeKey.split(',').includes(pingTarget.id);
    if (bout.status !== 'current' || !seated) setPingTarget(null);
  }, [pingTarget, bout.status, bout.ring_referee_id, sideJudgeKey]);

  // Pool membership
  const inRefereePool = bout.user_in_referee_pool === true;
  const inJuryPool    = bout.user_in_jury_pool    === true;

  // Load tournament referees when jury opens the ring-ref editor
  const tournamentId = bout?.tournament_id;
  const { data: tournamentReferees } = useLoad(
    editingRingRef && tournamentId ? () => getTournamentReferees(tournamentId).then(r => r.data as TournamentReferee[]) : null,
    [tournamentId, bout?.ring_referee_id],
    {
      context: 'BoutOfficials:referees',
      keepData: true,   // a socket change of the ring referee keeps the options on screen
      initial: [] as TournamentReferee[],
      onData: () => setSelectedRingRefId(bout.ring_referee_id || ''),
    },
  );

  // The ring referee options are the pool members who are not side judges and
  // not the jury of this bout (#454). Only the jury opens this list, and it
  // offered the jury's own name: on 2026-09-26 the server refused that pick
  // five times (JURY_CANNOT_BE_RING_REF).
  const sideJudgeIds = sideJudges.map(r => r.id);
  const ringRefOptions = tournamentReferees.filter(r => !sideJudgeIds.includes(r.id) && r.id !== bout.jury_id);

  async function handleClaimJury() {
    await jurySeat.run(async () => {
      setJuryError('');
      try {
        await assignBoutJury(bout.tournament_id, bout.id);
      } catch (err) {
        setJuryError(inlineError(err, t.bout.juryClaimFailed));
        return;
      }
      // #664 — the claim committed: a failed read warns, it never shows "Could not assign".
      // The rejoin makes the server check the new seat. It adds no reference (F49).
      rejoinBoutOfficialsRoom(bout.id);
      await afterWrite({ source: 'BoutOfficials:claimJury', success: t.bout.juryClaimed, refresh });
    });
  }

  async function handleUnclaimJury() {
    await jurySeat.run(async () => {
      setJuryError('');
      try {
        await removeBoutJury(bout.tournament_id, bout.id);
      } catch (err) {
        setJuryError(inlineError(err, t.bout.juryUnclaimFailed));
        return;
      }
      await afterWrite({ source: 'BoutOfficials:unclaimJury', success: t.bout.juryUnclaimed, refresh });
    });
  }

  async function handleClaimSide() {
    await sideSeat.run(async () => {
      setSideError('');
      try {
        await assignSelfToBout(bout.id);
      } catch (err) {
        setSideError(inlineError(err, t.bout.sideJoinFailed));
        return;
      }
      rejoinBoutOfficialsRoom(bout.id);
      await afterWrite({ source: 'BoutOfficials:claimSide', success: t.bout.joinedSideJudges, refresh });
    });
  }

  async function handleUnclaimSide() {
    await sideSeat.run(async () => {
      setSideError('');
      try {
        await removeSelfFromBout(bout.id);
      } catch (err) {
        setSideError(inlineError(err, t.bout.sideLeaveFailed));
        return;
      }
      await afterWrite({ source: 'BoutOfficials:unclaimSide', success: t.bout.leftSideJudges, refresh });
    });
  }

  async function handleSaveRingRef() {
    await ringRefSave.run(async () => {
      setRingRefError('');
      try {
        await assignRingReferee(bout.id, selectedRingRefId);
      } catch (err) {
        setRingRefError(inlineError(err, t.bout.ringRefSaveFailed));
        return;
      }
      // The save committed, so the editor closes also when the read fails.
      await afterWrite({ source: 'BoutOfficials:ringRef', refresh });
      setEditingRingRef(false);
    });
  }

  // The actions of the officials are 44 px touch targets (operator,
  // 2026-09-25): they are tapped at ringside, beside the tall point and count
  // buttons, and btn-sm gave about 30 px. code-patterns.md § Button sizing.
  // Each action sits on the right of its row (Edit ring referee, Claim or
  // Unclaim jury, Join or Leave), so the three rows line up.
  return (
    <div data-testid="bout-officials" className="border-t border-gray-100 pt-2 space-y-3">
      {/* Ring referee */}
      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t.bout.ringReferee}</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {bout.ring_ref_first ? (
            <OfficialPill role="ring" name={personName(bout.ring_ref_first, bout.ring_ref_last)}
              isYou={seats.ringReferee}>
              {seats.jury && bout.status === 'current' && bout.ring_referee_id && (
                <button onClick={() => setPingTarget({
                  id: bout.ring_referee_id!, first_name: bout.ring_ref_first ?? '',
                  defaultText: t.bout.pingRingReferee.replace('{n}', String(bout.bout_number)),
                })}
                  className="p-3 -m-3 text-gray-300 hover:text-amber-500 transition-colors"
                  title={t.bout.notifyOfficial} aria-label={t.bout.notifyOfficial}>
                  <BellIcon className="w-3 h-3" filled />
                </button>
              )}
            </OfficialPill>
          ) : (
            <span className="text-xs text-gray-400 italic px-1">{t.bout.noRingReferee}</span>
          )}
          {seats.jury && boutActive && !hasVerdict && !editingRingRef && (
            <button onClick={() => setEditingRingRef(true)} data-testid="ring-ref-edit"
              className="ml-auto btn-outline-orange min-h-11 font-semibold">
              <PencilIcon /> {t.bout.editRingReferee}
            </button>
          )}
        </div>
      </div>

      {/* Inline ring referee editor: the colours of the ring referee pill
          (orange), not of the jury who opens it. */}
      {editingRingRef && (
        <div data-testid="ring-ref-panel" className="bg-orange-50 border border-orange-300 rounded-lg p-3 space-y-2">
          <div className="text-xs font-semibold text-orange-800">{t.bout.editRingReferee}</div>
          <select
            value={selectedRingRefId}
            onChange={e => setSelectedRingRefId(e.target.value)}
            className="input-orange">
            <option value="">— {t.bout.noRingReferee} —</option>
            {ringRefOptions.map(r => (
              <option key={r.id} value={r.id}>
                {personName(r.first_name, r.last_name)}
                {/* #603 — a native option draws no SVG: the mark is a word. */}
                {r.id === bout.ring_referee_id ? ` · ${t.bout.ringRefAssignedMark}` : ''}
              </option>
            ))}
          </select>
          {ringRefError && <Notice tone="error" plain size="xs">{ringRefError}</Notice>}
          <div className="flex gap-2">
            <button onClick={handleSaveRingRef} disabled={ringRefSave.busy || !selectedRingRefId}
              data-testid="ring-ref-save" className="btn-orange min-h-11">
              {ringRefSave.busy ? t.common.loading : t.common.save}
            </button>
            <button onClick={() => { setEditingRingRef(false); setRingRefError(''); }}
              className="btn-outline-orange min-h-11">
              {t.common.cancel}
            </button>
          </div>
        </div>
      )}

      {/* Jury */}
      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t.bout.jury}</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {bout.jury_first ? (
            <OfficialPill role="jury" name={personName(bout.jury_first, bout.jury_last)} isYou={seats.jury} />
          ) : (
            <span className="text-xs text-gray-400 italic px-1">{t.bout.noJury}</span>
          )}
          {isJury && boutActive && (
            <div className="ml-auto flex gap-1.5">
              {seats.jury ? (
                // Unclaim takes the theme of Claim: the purple of the jury.
                <button onClick={handleUnclaimJury} disabled={jurySeat.busy} data-testid="jury-action"
                  className="btn-outline-purple min-h-11 font-semibold">
                  {jurySeat.busy ? '...' : t.bout.unclaimJury}
                </button>
              ) : !juryAssigned && inRefereePool && inJuryPool ? (
                <button onClick={handleClaimJury} disabled={jurySeat.busy} data-testid="jury-action"
                  className="btn-outline-purple min-h-11 font-semibold">
                  {jurySeat.busy ? '...' : t.bout.claimJury}
                </button>
              ) : null}
            </div>
          )}
        </div>
        {juryError && <Notice tone="error" plain size="xs" className="mt-1">{juryError}</Notice>}
      </div>

      {/* Side judges */}
      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
          {t.bout.sideJudges} ({sideJudges.length}/{MAX_SIDE_JUDGES})
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {sideJudges.map(ref => (
            <OfficialPill key={ref.id} role="side" name={personName(ref.first_name, ref.last_name)}
              isYou={ref.id === user?.id}>
              {seats.jury && bout.status === 'current' && ref.id !== user?.id && (
                <button onClick={() => setPingTarget({
                  id: ref.id, first_name: ref.first_name,
                  defaultText: t.bout.pingSideJudge.replace('{n}', String(bout.bout_number)),
                })}
                  className="p-3 -m-3 text-gray-300 hover:text-amber-500 transition-colors"
                  title={t.bout.notifyOfficial} aria-label={t.bout.notifyOfficial}>
                  <BellIcon className="w-3 h-3" filled />
                </button>
              )}
            </OfficialPill>
          ))}
          {canJudge && boutActive && (
            seats.sideJudge ? (
              <button onClick={handleUnclaimSide} disabled={sideSeat.busy} data-testid="side-judge-action"
                className="ml-auto btn-outline-red min-h-11 font-semibold">
                {sideSeat.busy ? '\u2026' : <><XMarkIcon className="w-4 h-4" />{t.bout.leaveSide}</>}
              </button>
            ) : inRefereePool && !sideJudgeFull && !seats.ringReferee ? (
              // The ring referee is never a side judge of the same bout
              // (SIDE_JUDGE_CANNOT_BE_RING_REF): the button showed to the ring
              // referee, and the server refused the click (2026-09-26).
              <button onClick={handleClaimSide} disabled={sideSeat.busy} data-testid="side-judge-action"
                className="ml-auto btn-outline-green min-h-11 font-semibold">
                {sideSeat.busy ? '\u2026' : <><UserPlusIcon className="w-4 h-4" />{t.bout.joinSide}</>}
              </button>
            ) : null
          )}
        </div>
        {sideError && <Notice tone="error" plain size="xs" className="mt-1">{sideError}</Notice>}
      </div>

      {/* Pool gate notice -- jury only */}
      {isJury && boutActive && inRefereePool && !inJuryPool && !seats.jury && !juryAssigned && (
        <Notice tone="warning" size="xs" icon={ClipboardIcon} className="leading-relaxed">
          {t.bout.notInJuryPool}{' '}
          <Link to={`/tournaments/${bout.tournament_id}?tab=officials`}
            className="font-semibold underline underline-offset-2">{t.bout.openOfficialsTab}</Link>
        </Notice>
      )}

      {/* #557, #664 — the jury pings an official of this live bout. The answer shows: an empty catch
          hid the 403 that a user with the jury and the tournament_manager roles got on a tournament
          of another manager. */}
      {pingTarget && (
        <PingModal target={pingTarget} title={t.roles.jury} defaultText={pingTarget.defaultText}
          url={`/bouts/${bout.id}`} boutId={bout.id} tournamentId={bout.tournament_id}
          onClose={() => setPingTarget(null)} />
      )}
    </div>
  );
}

type OfficialRole = 'ring' | 'jury' | 'side';

// One pill for every official (operator, 2026-09-25): the same height, the
// same "(You)" mark, and the color of the role on the border, the dot and the
// text. The ring referee is orange, the jury purple, a side judge green. Your
// own pill has a tinted background. min-h-7 holds the height with or without
// the Notify bell.
// The ring referee pill is orange and the jury pill purple at all times
// (operator, 2026-09-29). The own pill takes a stronger tint of the colour,
// so "you" stays visible.
const PILL_ROLE: Record<OfficialRole, { pill: string; dot: string; bg: string; you: string }> = {
  ring: { pill: 'border-orange-300 text-orange-800', dot: 'bg-orange-500', bg: 'bg-orange-50', you: 'bg-orange-100' },
  jury: { pill: 'border-purple-300 text-purple-800', dot: 'bg-purple-500', bg: 'bg-purple-50', you: 'bg-purple-100' },
  side: { pill: 'border-green-300 text-green-800',   dot: 'bg-green-500',  bg: 'bg-gray-50',   you: 'bg-green-50' },
};

function OfficialPill({ role, name, isYou, children }: {
  role: OfficialRole;
  name: string;
  isYou: boolean;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const c = PILL_ROLE[role];
  return (
    <div data-testid={`official-pill-${role}`}
      className={`flex items-center gap-1.5 min-h-7 px-2.5 rounded-full text-xs font-medium border ${c.pill} ${
        isYou ? c.you : c.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${c.dot}`} />
      {name}
      {isYou && <span className="opacity-50 ml-0.5">({t.common.you})</span>}
      {children}
    </div>
  );
}
